import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex items-center justify-center p-2 sm:p-4 lg:p-6 xl:p-8 bg-gray-100 flex-1" };
const _hoisted_2 = { class: "bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg sm:shadow-xl lg:shadow-2xl p-0 max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl w-full mx-auto" };
const _hoisted_3 = {
    class: "rounded-t-lg sm:rounded-t-xl lg:rounded-t-2xl px-4 sm:px-6 md:px-8 pt-8 sm:pt-10 md:pt-12 text-center",
    style: { "background": "radial-gradient(56.52% 80% at 10% 0%, #ffcea8 0%, #fff18b 40%, #ffffff 100%)" }
};
const _hoisted_4 = { class: "text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2" };
const _hoisted_5 = { class: "text-sm sm:text-base md:text-lg text-gray-700" };
const _hoisted_6 = { class: "flex flex-col items-center justify-center pt-8 sm:pt-10 md:pt-12 pb-6 sm:pb-8 px-4 sm:px-6 md:px-8" };
const _hoisted_7 = { class: "text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2" };
const _hoisted_8 = { class: "text-gray-500 text-xs sm:text-sm mt-2 text-center px-2" };
import { ref, onMounted, watch } from "vue";
import { useAuth } from "@/shared/composables/useAuth";
import { useI18n } from "vue-i18n";
import Loader from "@/presentation/components/Loader.vue";
import { OnboardingUseCases } from "@/application/use-cases/OnboardingUseCases";
import { useErrorHandler, ERROR_CONTEXTS } from "@/shared/utils/errorHandler";
import { useLogger } from "@/shared/composables/useLogger";
export default /*@__PURE__*/ _defineComponent({
    __name: 'Onboarding',
    setup(__props) {
        const logger = useLogger();
        // Use i18n
        const { t } = useI18n();
        // Reactive state
        const isProcessing = ref(false);
        const loadingMessage = ref("");
        const progressPercentage = ref(0);
        // Initialize use cases
        const onboardingUseCases = new OnboardingUseCases();
        // Error handling
        const { handleError } = useErrorHandler();
        // Auth composable
        const { isAuthenticated, isInitialized, isLoading } = useAuth();
        // Create Adyen onboarding link and redirect
        async function createAdyenLink() {
            progressPercentage.value = 20;
            // Call the real API endpoint
            const returnUrl = await onboardingUseCases.createAdyenOnboardingLink();
            progressPercentage.value = 80;
            loadingMessage.value = t("onboarding.redirecting");
            // Wait a moment before redirecting
            setTimeout(() => {
                progressPercentage.value = 100;
                window.location.href = returnUrl;
            }, 500);
        }
        // Handle post-authentication flow
        const handlePostAuthentication = async () => {
            try {
                isProcessing.value = true;
                progressPercentage.value = 0;
                loadingMessage.value = t("onboarding.processingRequest");
                // Create Adyen onboarding link
                await createAdyenLink();
            }
            catch (err) {
                handleError(err, ERROR_CONTEXTS.ONBOARDING_PROCESS);
                isProcessing.value = false;
                progressPercentage.value = 0;
                logger.debug("Post-authentication error:", err);
            }
        };
        // Note: Login is now handled by router guard since onboarding route requires authentication
        // Watch for authentication changes
        watch(() => isAuthenticated.value, (newValue) => {
            if (newValue && isInitialized.value) {
                handlePostAuthentication();
            }
        });
        // Watch for authentication changes
        watch(() => isAuthenticated.value, (newValue) => {
            if (newValue && isInitialized.value) {
                logger.debug("Onboarding: User authenticated, starting post-auth flow");
                handlePostAuthentication();
            }
        });
        // Watch for initialization changes
        watch(() => isInitialized.value, (newValue) => {
            if (newValue && isAuthenticated.value) {
                logger.debug("Onboarding: Auth initialized and user authenticated, starting post-auth flow");
                handlePostAuthentication();
            }
        });
        // Initialize on mount
        onMounted(() => {
            logger.debug("Onboarding mounted, auth state:", {
                isAuthenticated: isAuthenticated.value,
                isInitialized: isInitialized.value,
                isLoading: isLoading.value,
            });
            // If already authenticated when component mounts, start the flow
            if (isAuthenticated.value && isInitialized.value) {
                logger.debug("Onboarding: User already authenticated on mount, starting post-auth flow");
                handlePostAuthentication();
            }
            // Note: No need to manually trigger login - router guard will handle it
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("h1", _hoisted_4, _toDisplayString(_unref(t)("onboarding.welcome")), 1 /* TEXT */),
                        _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)("onboarding.subtitle")), 1 /* TEXT */)
                    ]),
                    _createElementVNode("div", _hoisted_6, [
                        _createVNode(Loader, { class: "mb-6 sm:mb-8 scale-125 sm:scale-150 pb-4 sm:pb-6" }),
                        _createElementVNode("div", _hoisted_7, [
                            _createTextVNode(_toDisplayString(progressPercentage.value), 1 /* TEXT */),
                            _cache[0] || (_cache[0] = _createElementVNode("span", { class: "align-super text-lg sm:text-xl md:text-2xl font-bold" }, "%", -1 /* CACHED */))
                        ]),
                        _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)("onboarding.processingRequest")), 1 /* TEXT */)
                    ])
                ])
            ]));
        };
    }
});
